import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../../../routes';
import useNumCartItems from '../../../../hooks/useNumCartItems';
import { useCustomer } from '../../../../contexts/CustomerContext';
import logo from '../../../../assets/logo.jpg';
import NavDrawerItems from '../../../Nav/v2/NavDrawerItems';
import styles from './GiftHeader.module.scss';

/**
 * The Announcements & Gifts header — THIS PAGE'S ONLY.
 *
 * ⚠️ IT IS NOT THE SITE HEADER. `Nav/v2/Nav.jsx` is, and it is rendered by
 * CustomerApp on all 72 `/c/*` routes; editing it would restyle the cart, the
 * checkout, package selection and every appointment screen. This storefront
 * carries its own instead, and HolidayBuilder hides the shared one while the
 * page is mounted.
 *
 * ⚠️ THREE CONTROLS, AT EVERY WIDTH: hamburger, logo, cart. Nothing else.
 * This is a CHECKOUT STEP, not a shop front — a customer who has picked a
 * package and is choosing add-ons should not be offered six ways to leave. The
 * destinations are all still here, one press away behind the hamburger; they
 * are just no longer competing with "Continue to checkout".
 *
 * That is why there is no desktop/phone branch any more. The header used to
 * render a wide bar with six links above 1024px and this compact one below it,
 * which is two layouts to keep honest and a breakpoint to argue about. One bar
 * cannot drift from itself. Only its height changes (60 / 72).
 *
 * ⚠️ THE MENU BEHIND THE HAMBURGER IS THE SITE'S OWN (2026-09-29). It renders
 * `Nav/v2/NavDrawerItems`, the same component the home page's MobileNav
 * renders, so this page cannot offer a different set of destinations from the
 * rest of the site. It used to hand-maintain eight links of its own, one of
 * which 404'd. Add a link in NavDrawerItems, never here.
 *
 * What moved OUT of the header: Contact Us, Register, Privacy & Data Policy and
 * Guarantee. On the shared nav those four sit in the top-right utility row,
 * where "Privacy & Data Policy" runs out of room, truncates to "Privacy & Data
 * Po…" and collides with the cart. They are footer material — secondary links
 * nobody navigates to mid-purchase — so GiftFooter carries them.
 */

// One place for the item count's wording, because the badge and the accessible
// label must never disagree: a screen reader that says "3 items" over a badge
// reading 2 is worse than no badge.
const cartLabel = (count) => (
  count === 1 ? 'Cart, 1 item' : `Cart, ${count} items`
);

function MenuIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg
      className="h-6 w-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path
        d="M3 4h2.2l2.3 11.2A2 2 0 009.8 17h7.9a2 2 0 001.9-1.5L21 8H6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="20" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18" cy="20" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * The primary destinations, in order. ONE list: the desktop bar renders it
 * across, the phone menu renders it down. Two copies would drift the first time
 * a destination changed.
 *
 * `to` is an in-app route, `href` a Rails page.
 */

// 15px / 600, dark. `inline-flex items-center` so the 44px minimum tap target
// the page sets on every link lands as a centred hit area rather than as text
// sitting at the top of a 44px box.
  + 'text-gift-ink no-underline transition hover:text-gift-accent';

export default function GiftHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { customer } = useCustomer();
  const { data: numItems } = useNumCartItems({
    id: customer?.cart?.id,
    email: customer?.cart?.email || customer?.email,
  });

  // `useNumCartItems` is disabled without BOTH a cart id and an email, so an
  // anonymous shopper gets undefined rather than 0.
  const count = Number(numItems) || 0;

  // Matches the shared nav's own test, so the link agrees with the rest of the
  // site about whether this browser is signed in.
  const isSignedIn = !!document.querySelector('#current-user');

  const cartBadge = count > 0 && (
    <span
      aria-hidden="true"
      className={'absolute -right-2 -top-1 inline-flex h-[18px] min-w-[18px] items-center '
        + 'justify-center rounded-gift-pill bg-gift-accent px-1 text-[11px] font-bold '
        + 'leading-none text-white'}
    >
      {count}
    </span>
  );

  return (
    <header className="w-full border-0 border-b border-solid border-gift-line bg-white">
      {/* 60px on a phone, 72 above it -- the only thing that still changes with
          width. The CONTENT does not: one bar, three controls, every width. */}
      <div
        className={'mx-auto flex h-[60px] max-w-[1440px] items-center justify-between px-4 '
          + 'md:h-[72px] md:px-10 lg:px-16'}
      >
        {/* HAMBURGER LEFT OF THE LOGO, as one group on the left edge. */}
        <div className="flex min-w-0 items-center gap-3 md:gap-4">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="gift-menu"
            /* A CONSTANT NAME, with `aria-expanded` carrying the state. It used
               to flip to "Close menu", which collided with the drawer's own X. */
            aria-label="Menu"
            className="inline-flex items-center border-0 bg-transparent p-0 text-gift-ink"
          >
            <MenuIcon />
          </button>

          <Link
            to="/"
            aria-label="Shoob Photography home"
            className="inline-flex shrink-0 items-center"
          >
            <img src={logo} alt="Shoob Photography" className="h-8 w-auto md:h-10" />
          </Link>
        </div>

        {/* The count is on the LABEL, not just in the badge -- the badge is a
            visual, and a cart link that announces itself as "Cart" alone tells
            a screen-reader user nothing about what is in it. */}
        <Link
          to={routes.cart()}
          aria-label={cartLabel(count)}
          className={'relative inline-flex shrink-0 items-center text-gift-ink transition '
            + 'hover:text-gift-accent'}
        >
          <CartIcon />
          {cartBadge}
        </Link>
      </div>

      {/* ⚠️ THE DESTINATIONS ARE THE SITE'S, NOT THIS PAGE'S. This renders
          `Nav/v2/NavDrawerItems` — the same component the home page's hamburger
          renders — so the two cannot offer different sets. Alex, 2026-09-29:
          "the hamburger menu already exists on the home page, so that is the
          exact one for this page as well", no new or different links. This page
          used to hand-maintain eight of its own, one of which ("Help" ->
          /frequently-asked-questions) has no Rails route and 404s.
          ADD A LINK IN NavDrawerItems, NEVER HERE.

          A disclosure panel under the bar rather than the overlay the home page
          slides in: those overlay styles are declared phone-only, and this
          header carries a hamburger at EVERY width. See GiftHeader.module.scss. */}
      {menuOpen && (
        <nav
          id="gift-menu"
          aria-label="Primary"
          className="border-0 border-t border-solid border-gift-line px-4 md:px-10 lg:px-16"
        >
          <ul className={styles.panel}>
            <NavDrawerItems isSignedIn={isSignedIn} onClose={() => setMenuOpen(false)} />
          </ul>
        </nav>
      )}
    </header>
  );
}

export { cartLabel };

/**
 * Is this the wide layout?
 *
 * ⚠️ THE HEADER NO LONGER USES THIS. It renders one bar at every width, so
 * there is no band for it to pick. What still needs it is HolidayBuilder's
 * effect that hides the SHARED site nav: `Nav/v2/Nav.jsx` swaps between its
 * desktop and mobile components at a breakpoint, mounting a different <nav>
 * node, and the newly mounted one has to be hidden too. The effect re-runs on
 * this value for that reason alone.
 *
 * ⚠️ NOT the shared `useBreakpoints` hook, and that is the whole point. Its
 * bands are inclusive at the top (md is <=768), so at exactly 768px it said
 * "phone" while every `md:` rule in the CSS said "tablet". One media query,
 * quoted from the same breakpoint the stylesheet uses, cannot disagree with it.
 */
export const DESKTOP_QUERY = '(min-width: 1024px)';

export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(DESKTOP_QUERY).matches
      : true),
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);

    setIsDesktop(mql.matches);
    // addListener is the Safari <14 spelling; still in the wild on older iPads.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);

    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isDesktop;
};
